import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Box,
  Grid,
  Stack,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
} from "@mui/material";
import { priorityConfig } from "./TaskCard";
import {
  CheckCircle2,
  Clock,
  TrendingUp,
  AlertTriangle,
  Award,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";

function StatCard({ title, value, unit = "", icon: Icon, color = "#6C5CE7", bgcolor = "#F0EEFF" }) {
  return (
    <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", borderRadius: "12px" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography fontSize={13} fontWeight={600} color="#717182">
            {title}
          </Typography>
          <Box sx={{ p: 1, borderRadius: "8px", bgcolor, color }}>
            <Icon size={18} />
          </Box>
        </Stack>
        <Typography fontSize={28} fontWeight={700} color="#1A1A24">
          {value}
          {unit && (
            <span style={{ fontSize: 14, fontWeight: 500, color: "#717182", marginLeft: "4px" }}>
              {unit}
            </span>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function DashboardView({ tasks = [], currentUser }) {
  // Filter tasks assigned to the current user OR where they are a collaborator
  const myTasks = tasks.filter(
    (t) =>
      t.assignee?.id === currentUser.id ||
      t.assignee?.name === "You" ||
      t.assignee?.name === currentUser.name ||
      (Array.isArray(t.collaborators) && t.collaborators.some((c) => c.id === currentUser.id))
  );

  // Calculations
  const totalTasks = myTasks.length;
  const completedTasks = myTasks.filter((t) => t.status === "completed").length;
  const pendingTasks = totalTasks - completedTasks;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Overdue calculation
  const overdueTasks = myTasks.filter((t) => {
    if (!t.dueDate || t.status === "completed") return false;
    try {
      const dueDate = new Date(t.dueDate);
      if (isNaN(dueDate.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDate < today;
    } catch {
      return false;
    }
  }).length;

  // Status Distribution
  const statusCounts = { todo: 0, in_progress: 0, review: 0, completed: 0 };
  myTasks.forEach((t) => {
    if (statusCounts[t.status] !== undefined) {
      statusCounts[t.status]++;
    }
  });
  const statusData = [
    { name: "To Do", value: statusCounts.todo, color: "#9CA3AF" },
    { name: "In Progress", value: statusCounts.in_progress, color: "#3B82F6" },
    { name: "Review", value: statusCounts.review, color: "#F59E0B" },
    { name: "Completed", value: statusCounts.completed, color: "#10B981" },
  ].filter((d) => d.value > 0);

  // Priority Breakdown
  const priorityCounts = { high: 0, medium: 0, low: 0 };
  myTasks.forEach((t) => {
    if (priorityCounts[t.priority] !== undefined) {
      priorityCounts[t.priority]++;
    }
  });
  const priorityData = [
    { name: "High", value: priorityCounts.high, color: "#EF4444" },
    { name: "Medium", value: priorityCounts.medium, color: "#F59E0B" },
    { name: "Low", value: priorityCounts.low, color: "#3B82F6" },
  ].filter((d) => d.value > 0);

  // Busiest Days (dynamic calculation by due date day of the week)
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayCounts = { Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0, Saturday: 0, Sunday: 0 };
  
  myTasks.forEach((t) => {
    if (t.dueDate && t.status !== "completed") {
      try {
        const d = new Date(t.dueDate);
        if (!isNaN(d.getTime())) {
          const dayName = daysOfWeek[d.getDay()];
          dayCounts[dayName]++;
        }
      } catch (e) {
        console.error(e);
      }
    }
  });

  const busiestDaysData = [
    { day: "Mon", tasks: dayCounts.Monday },
    { day: "Tue", tasks: dayCounts.Tuesday },
    { day: "Wed", tasks: dayCounts.Wednesday },
    { day: "Thu", tasks: dayCounts.Thursday },
    { day: "Fri", tasks: dayCounts.Friday },
    { day: "Sat", tasks: dayCounts.Saturday },
    { day: "Sun", tasks: dayCounts.Sunday },
  ];

  // Upcoming high-priority tasks (limit to 3)
  const upcomingSchedule = myTasks
    .filter((t) => t.status !== "completed" && t.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 3);

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 3, bgcolor: "#F8FAFC" }}>
      <Box sx={{ maxWidth: "1280px", mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
        
        {/* Title */}
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1A1A24">
            Analytics Dashboard
          </Typography>
          <Typography fontSize={13} color="#717182" mt={0.5}>
            Real-time performance and task distribution statistics for {currentUser.name}
          </Typography>
        </Box>

        {/* Stats Row */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Tasks Completed"
              value={completedTasks}
              icon={CheckCircle2}
              color="#10B981"
              bgcolor="#E6F4EA"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Tasks"
              value={pendingTasks}
              icon={Clock}
              color="#3B82F6"
              bgcolor="#E8F0FE"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Completion Rate"
              value={completionRate}
              unit="%"
              icon={TrendingUp}
              color="#6C5CE7"
              bgcolor="#F0EEFF"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Overdue Tasks"
              value={overdueTasks}
              icon={AlertTriangle}
              color="#EF4444"
              bgcolor="#FCE8E6"
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3}>
          {/* Busiest Days Chart */}
          <Grid item xs={12} lg={7}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none" }}>
              <CardHeader 
                title={
                  <Typography fontSize={15} fontWeight={600} color="#1A1A24">
                    Busiest Days (Pending Tasks Due)
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box sx={{ height: 300, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={busiestDaysData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} tickLine={false} />
                      <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} allowDecimals={false} />
                      <Tooltip
                        cursor={{ fill: "rgba(108,92,231,0.04)" }}
                        contentStyle={{
                          backgroundColor: "#white",
                          border: "1px solid #E2E8F0",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          fontSize: 12,
                        }}
                      />
                      <Bar dataKey="tasks" fill="#6C5CE7" radius={[4, 4, 0, 0]} barSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Breakdown (Pie Chart) */}
          <Grid item xs={12} md={6} lg={5}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none", height: "100%" }}>
              <CardHeader
                title={
                  <Typography fontSize={15} fontWeight={600} color="#1A1A24">
                    Task Status Distribution
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", pb: 3 }}>
                {statusData.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography fontSize={13} color="#717182">No task data available</Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ height: 200, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={4}
                            dataKey="value"
                          >
                            {statusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              border: "1px solid #E2E8F0",
                              borderRadius: "8px",
                              fontSize: 12,
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Stack gap={1.25} mt={2} px={1}>
                      {statusData.map((item) => (
                        <Box key={item.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                            <Typography fontSize={13} color="#4A4A5A">
                              {item.name}
                            </Typography>
                          </Box>
                          <Typography fontSize={13} fontWeight={600} color="#1A1A24">
                            {item.value} {item.value === 1 ? "task" : "tasks"} ({Math.round((item.value / totalTasks) * 100)}%)
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom Row */}
        <Grid container spacing={3}>
          {/* Priority Breakdown (Pie/Doughnut) */}
          <Grid item xs={12} md={6}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none" }}>
              <CardHeader
                title={
                  <Typography fontSize={15} fontWeight={600} color="#1A1A24">
                    Priority Distribution
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {priorityData.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography fontSize={13} color="#717182">No task priorities available</Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ height: 180, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={priorityData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={65}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {priorityData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: "8px", fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Stack gap={1.25} px={1}>
                      {priorityData.map((item) => (
                        <Box key={item.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                            <Typography fontSize={13} color="#4A4A5A">
                              {item.name} Priority
                            </Typography>
                          </Box>
                          <Typography fontSize={13} fontWeight={600} color="#1A1A24">
                            {item.value} ({Math.round((item.value / totalTasks) * 100)}%)
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Upcoming Schedule */}
          <Grid item xs={12} md={6}>
            <Card sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", boxShadow: "none", height: "100%" }}>
              <CardHeader
                title={
                  <Typography fontSize={15} fontWeight={600} color="#1A1A24">
                    Upcoming High-Priority Schedule
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Stack gap={2}>
                  {upcomingSchedule.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4, bgcolor: "#white", borderRadius: "8px" }}>
                      <Typography fontSize={13} color="#717182">No upcoming pending tasks</Typography>
                    </Box>
                  ) : (
                    upcomingSchedule.map((t) => {
                      const priorityColor = priorityConfig[t.priority]?.color || "#717182";
                      return (
                        <Box
                          key={t.id}
                          sx={{
                            p: 2,
                            bgcolor: "white",
                            borderRadius: "10px",
                            border: "1px solid rgba(0,0,0,0.05)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            "&:hover": { bgcolor: "#F1F5F9" },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Typography fontSize={13.5} fontWeight={600} color="#1A1A24" noWrap sx={{ maxWidth: "75%" }}>
                              {t.title}
                            </Typography>
                            <Chip
                              label={t.priority}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                bgcolor: priorityConfig[t.priority]?.bg || "#ECECF0",
                                color: priorityColor,
                                fontWeight: 600,
                                borderRadius: "4px",
                              }}
                            />
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontSize={11} color="#EF4444" fontWeight={500}>
                              Due: {new Date(t.dueDate).toLocaleDateString()}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <Avatar
                                src={t.assignee?.image ? `https://mindsinaction.com.na/assets/media/pictures/${t.assignee.image}` : undefined}
                                sx={{ width: 16, height: 16, fontSize: 8 }}
                              >
                                {t.assignee?.avatar || "?"}
                              </Avatar>
                              <Typography fontSize={11} color="#717182">
                                {t.assignee?.name}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
